/**
 * Purchase orders, To buy, "Order and wait" (BLU-1506): the first list on the
 * tab. Blanks from an Order and wait supplier are bought BEFORE batching; the
 * orders that need them wait, and move to Ready to batch by themselves when
 * the box arrives.
 *
 * One card per supplier at the floor picked in the action bar (the tab is one
 * floor, so a card is one (supplier, floor)):
 *
 *   sent orders     On the way, due X / Late; Arrived opens the Arrived sheet
 *   the next order  Not sent yet, "N blanks · M orders", "Send by 2:00 pm"
 *                   for a supplier that sends daily (amber once that time has
 *                   passed at the floor and nothing went to the supplier
 *                   today; "tomorrow" once something did), its blanks with
 *                   their out / short marks; Send order opens the Send dialog
 *   spares          blanks left over after an order changed: the next order
 *                   for the blank takes them first
 *
 * An order placed with the supplier: the chip says Placing order, Checking with
 * {supplier} or Not placed, Arrived hides while nothing can have shipped, and a
 * count says how many need a look. Until the supplier has it, it does not read as
 * coming: no due date, and not in the on the way or late counts. One read of the floor's unsettled rows
 * (`useOpenApiOrders`), joined here by PO; a placed order is not in it, so it stays
 * On the way.
 *
 * Send and Arrived show only for `purchase_orders.manage`; their accessible
 * name is the visible words, and the card's heading (supplier and PO number)
 * describes them (WCAG 2.5.3). The list is the floor owner's: anyone else
 * gets FORBIDDEN, and this section then renders nothing (Below keep at least
 * already says whose floor it is). An org that buys no supplier this way, with
 * nothing still on the way or spare, sees nothing either.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCheck, Clock, Send } from 'lucide-react';
import type { OrderAndWaitPo, OrderAndWaitSpare } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { StatusPill } from '@/components/shared/StatusPill';
import { useRole } from '@/hooks/use-role';
import { formatDayMonth, useDateFnsLocale } from '@/lib/format-dates';
import { cn } from '@/lib/utils';
import { useSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import { useOpenApiOrders, useOrderAndWaitCount, useOrderAndWaitPos } from '../hooks/useOrderAndWait';
import {
  ARRIVED_BUTTON_TONE,
  apiOrderChipStatus,
  blankLines,
  canMarkArrived,
  formatSendTime,
  nextBuildingPoNumber,
  sendByState,
  sumQty,
  supplierKey,
  suppliersSentToday,
  withApiOrderStatus,
} from '../lib/order-and-wait';
import { jitRowName } from '../lib/jit-lines';
import { OrderAndWaitLineMarks } from './OrderAndWaitLineMarks';
import { OrderAndWaitStatusChip } from './OrderAndWaitStatusChip';
import { PoNumberLink } from './PoNumberLink';
import { SendOrderDialog } from './SendOrderDialog';
import { ArrivedDialog } from './ArrivedDialog';

/** DESIGN.md section 3.4 button sizes, with the phone-sized hit target the To buy tab uses. */
const CARD_BUTTON = 'h-10 gap-1.5 px-3 text-xs sm:h-8';
const PRIMARY = cn(CARD_BUTTON, 'bg-threadheads-purple text-white hover:bg-threadheads-purple/90');
const ARRIVED = cn(CARD_BUTTON, ARRIVED_BUTTON_TONE);

interface SupplierGroup {
  key: string;
  supplierId: string | null;
  supplierName: string | null;
  /** Late first, then the earliest due. */
  sent: OrderAndWaitPo[];
  building: OrderAndWaitPo | null;
}

function groupBySupplier(purchaseOrders: readonly OrderAndWaitPo[]): SupplierGroup[] {
  const groups = new Map<string, SupplierGroup>();
  for (const po of purchaseOrders) {
    const key = supplierKey(po);
    let group = groups.get(key);
    if (!group) {
      group = { key, supplierId: po.supplierId, supplierName: po.supplierName, sent: [], building: null };
      groups.set(key, group);
    }
    if (po.status === 'draft') group.building = po;
    else group.sent.push(po);
  }
  for (const group of groups.values()) {
    group.sent.sort((a, b) => {
      const lateA = a.displayStatus === 'late' ? 0 : 1;
      const lateB = b.displayStatus === 'late' ? 0 : 1;
      if (lateA !== lateB) return lateA - lateB;
      return (a.arrivalEtaDate ?? '9999').localeCompare(b.arrivalEtaDate ?? '9999');
    });
  }
  return [...groups.values()];
}

/** The wall clock, re-read every minute, so "Send by" turns amber without a reload. */
function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

interface OrderAndWaitSectionProps {
  locationId: string;
  locationName: string | null;
  /** The floor's IANA timezone: "Send by" and "today" are the floor's. */
  locationTimezone: string | null;
  /** The PO list URL (this tab included), for each PO link's back button. */
  ordersReturnTo: string;
}

export function OrderAndWaitSection({
  locationId,
  locationName,
  locationTimezone,
  ordersReturnTo,
}: OrderAndWaitSectionProps) {
  const { t } = useTranslation('orders');
  const { can, isAdmin } = useRole();
  const canManage = can('purchase_orders.manage');
  // Lead times live on the supplier page, which only an admin may change.
  const canSetLeadTime = isAdmin();
  const canViewSuppliers = can('suppliers.view');
  const oaw = useOrderAndWaitPos(locationId);
  // Nothing waits on a floor with no PO, so the count is only asked once there is one.
  const count = useOrderAndWaitCount(locationId, oaw.purchaseOrders.length > 0);
  const openApiOrders = useOpenApiOrders(locationId, oaw.purchaseOrders.length > 0);
  const apiStatusByPo = useMemo(
    () => new Map(openApiOrders.rows.map((row) => [row.poId, row.status])),
    [openApiOrders.rows],
  );
  // Is any supplier bought this way? (The Orders page's own list: one request, shared.)
  const supplierList = useSuppliers({ includeInactive: true, enabled: canViewSuppliers });
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [arrivingId, setArrivingId] = useState<string | null>(null);

  const groups = useMemo(() => groupBySupplier(oaw.purchaseOrders), [oaw.purchaseOrders]);
  const sendingPo = oaw.purchaseOrders.find((po) => po.poId === sendingId) ?? null;
  // Only a draft: once it is sent, by anyone, the dialog has nothing left to offer.
  const sending = sendingPo?.status === 'draft' ? sendingPo : null;
  const arriving = oaw.purchaseOrders.find((po) => po.poId === arrivingId) ?? null;
  const arrivingNext = arriving ? nextBuildingPoNumber(oaw.purchaseOrders, arriving) : null;
  const floor = locationName ?? t('toBuy.thisFloor');

  if (oaw.forbidden) return null;

  // Only for an org that buys some supplier this way, or still has a box on the
  // way or spares from when it did. When the supplier list cannot say (no
  // permission, or its read failed), a failed Order and wait read still shows,
  // so a failure never passes for "nothing here".
  const usesOrderAndWait = supplierList.suppliers.some(
    (supplier) => supplier.is_active && supplier.supplyMode === 'order_and_wait',
  );
  const supplierModesKnown = canViewSuppliers && !supplierList.isLoading && !supplierList.error;
  const hasWork = groups.length > 0 || oaw.spares.length > 0;
  if (!usesOrderAndWait && !hasWork && !(oaw.failed && !supplierModesKnown)) return null;

  const building = groups.flatMap((group) => (group.building ? [group.building] : []));
  const sent = groups.flatMap((group) => group.sent);
  const coming = sent.filter((po) => apiOrderChipStatus(apiStatusByPo.get(po.poId)) === null);
  const stats = {
    toSend: building.reduce((total, po) => total + sumQty(po.lines), 0),
    onTheWay: coming.reduce((total, po) => total + sumQty(po.lines), 0),
    late: coming.filter((po) => po.displayStatus === 'late').length,
    waiting: count?.waitingOrders ?? null,
    needLook: openApiOrders.rows.filter((row) => row.status === 'refused' || row.status === 'unknown').length,
  };
  const empty = groups.length === 0 && oaw.spares.length === 0;

  return (
    <section aria-labelledby="to-buy-order-and-wait" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div>
          <h2 id="to-buy-order-and-wait" className="text-base font-semibold">
            {t('oaw.section.title')}
          </h2>
          <p className="text-sm text-muted-foreground">{t('oaw.section.help')}</p>
        </div>
        {!oaw.loading && !oaw.failed && !empty && (
          <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <Stat value={stats.toSend} label={t('oaw.stat.toSend', { count: stats.toSend })} />
            <Stat value={stats.onTheWay} label={t('oaw.stat.onTheWay', { count: stats.onTheWay })} />
            {stats.late > 0 && (
              <Stat value={stats.late} label={t('oaw.stat.late', { count: stats.late })} tone="warning" />
            )}
            {stats.needLook > 0 && (
              <Stat value={stats.needLook} label={t('oaw.stat.needLook', { count: stats.needLook })} tone="warning" />
            )}
            {stats.waiting !== null && (
              <Stat value={stats.waiting} label={t('oaw.stat.waiting', { count: stats.waiting })} />
            )}
          </dl>
        )}
      </div>

      {oaw.loading ? (
        <div className="space-y-2" role="status" aria-label={t('toBuy.loading')}>
          <Skeleton className="h-14 rounded-lg" />
          <Skeleton className="h-32 rounded-lg" />
        </div>
      ) : oaw.failed ? (
        <SectionError
          variant="compact"
          title={t('oaw.section.failed')}
          onRetry={oaw.retry}
          retryLabel={t('jit.retry')}
          retrying={oaw.retrying}
        />
      ) : empty ? (
        <NoteLine as="p" icon={CheckCheck}>
          {t('oaw.section.empty', { floor })}
        </NoteLine>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => (
            <Card key={group.key} className="divide-y overflow-hidden">
              {group.sent.map((po) => (
                <SentPoRow
                  key={po.poId}
                  po={po}
                  apiStatus={apiStatusByPo.get(po.poId) ?? null}
                  canManage={canManage}
                  canSetLeadTime={canSetLeadTime}
                  ordersReturnTo={ordersReturnTo}
                  onArrived={() => setArrivingId(po.poId)}
                />
              ))}
              {group.building && (
                <BuildingPo
                  po={group.building}
                  sent={group.sent}
                  canManage={canManage}
                  timezone={locationTimezone}
                  ordersReturnTo={ordersReturnTo}
                  onSend={() => setSendingId(group.building?.poId ?? null)}
                />
              )}
            </Card>
          ))}
          {oaw.spares.length > 0 && <SparesCard spares={oaw.spares} ordersReturnTo={ordersReturnTo} />}
        </div>
      )}

      <SendOrderDialog open={sending !== null} onOpenChange={(open) => !open && setSendingId(null)} po={sending} />
      <ArrivedDialog
        open={arriving !== null}
        onOpenChange={(open) => !open && setArrivingId(null)}
        po={arriving}
        locationId={locationId}
        nextPoNumber={arrivingNext}
      />
    </section>
  );
}

/** "3 blanks to send": the number first on screen, the label first for a screen reader. */
function Stat({ value, label, tone }: { value: number; label: string; tone?: 'warning' }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className={cn('order-last', tone === 'warning' && 'text-amber-800')}>{label}</dt>
      <dd className={cn('text-lg font-semibold tabular-nums text-foreground', tone === 'warning' && 'text-amber-800')}>
        {value}
      </dd>
    </div>
  );
}

function CountsLine({ po, className }: { po: OrderAndWaitPo; className?: string }) {
  const { t } = useTranslation('orders');
  const blanks = sumQty(po.lines);
  return (
    <span className={cn('text-xs tabular-nums text-muted-foreground', className)}>
      {t('oaw.card.blanks', { count: blanks })} · {t('oaw.card.orders', { count: po.waitingOrders })}
    </span>
  );
}

function SentPoRow({
  po,
  apiStatus,
  canManage,
  canSetLeadTime,
  ordersReturnTo,
  onArrived,
}: {
  po: OrderAndWaitPo;
  /** The order with the supplier, while it is not settled. */
  apiStatus: string | null;
  canManage: boolean;
  canSetLeadTime: boolean;
  ordersReturnTo: string;
  onArrived: () => void;
}) {
  const { t } = useTranslation('orders');
  const locale = useDateFnsLocale();
  const headingId = useId();
  const supplier = po.supplierName?.trim() || t('oaw.yourSupplier');
  const late = po.displayStatus === 'late';
  const sentOn = formatDayMonth(po.submittedDate, locale);
  const due = po.arrivalEtaDate ? formatDayMonth(po.arrivalEtaDate, locale) : null;
  const apiChip = apiOrderChipStatus(apiStatus);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
      <h3 id={headingId} className="text-sm font-semibold">
        {supplier} · <PoNumberLink poId={po.poId} poNumber={po.poNumber} ordersReturnTo={ordersReturnTo} />
      </h3>
      <OrderAndWaitStatusChip status={withApiOrderStatus(po.displayStatus, apiStatus)} supplierName={po.supplierName} />
      <span className="text-xs text-muted-foreground">
        {apiChip === 'not_placed'
          ? t('oaw.card.notPlacedLine')
          : apiChip
            ? t('oaw.card.notConfirmedLine', { sent: sentOn, supplier })
            : due
              ? late
                ? t('oaw.card.sentWasDue', { sent: sentOn, due })
                : t('oaw.card.sentDue', { sent: sentOn, due })
              : t('oaw.card.sentNoDue', { sent: sentOn })}
        {!apiChip && !due && po.supplierId && canSetLeadTime && (
          <>
            {' '}
            <Link to={`/suppliers/${po.supplierId}`} className="font-medium text-primary-strong underline-offset-4 hover:underline">
              {t('oaw.card.setLeadTime')}
            </Link>
          </>
        )}
      </span>
      <CountsLine po={po} className="sm:ml-auto" />
      {canManage && canMarkArrived(apiStatus) && (
        <Button
          type="button"
          variant="outline"
          className={ARRIVED}
          onClick={onArrived}
          aria-describedby={headingId}
        >
          <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
          {t('oaw.card.arrived')}
        </Button>
      )}
    </div>
  );
}

function BuildingPo({
  po,
  sent,
  canManage,
  timezone,
  ordersReturnTo,
  onSend,
}: {
  po: OrderAndWaitPo;
  /** The same supplier's sent boxes at this floor: one sent today moves the next send to tomorrow. */
  sent: readonly OrderAndWaitPo[];
  canManage: boolean;
  timezone: string | null;
  ordersReturnTo: string;
  onSend: () => void;
}) {
  const { t } = useTranslation('orders');
  const locale = useDateFnsLocale();
  const now = useMinuteClock();
  const headingId = useId();
  const supplier = po.supplierName?.trim() || t('oaw.yourSupplier');
  const lines = blankLines(po.lines);
  const sentToday = suppliersSentToday(sent, timezone, now).size > 0;
  const sendBy = lines.length > 0 ? sendByState(po.sendMode, po.sendTime, timezone, now, sentToday) : null;
  const sendByText = sendBy ? formatSendTime(sendBy.time, locale) : null;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
        <h3 id={headingId} className="text-sm font-semibold">
          {t('oaw.card.next', { supplier })} ·{' '}
          <PoNumberLink poId={po.poId} poNumber={po.poNumber} ordersReturnTo={ordersReturnTo} />
        </h3>
        <OrderAndWaitStatusChip status="not_sent" />
        {sendBy && sendByText && (
          <NoteLine
            icon={sendBy.passed ? AlertTriangle : Clock}
            tone={sendBy.passed ? 'warning' : 'muted'}
          >
            {sendBy.passed
              ? t('oaw.card.sendByPassed', { time: sendByText })
              : sendBy.tomorrow
                ? t('oaw.card.sendByTomorrow', { time: sendByText })
                : t('oaw.card.sendBy', { time: sendByText })}
          </NoteLine>
        )}
        <CountsLine po={po} className="sm:ml-auto" />
        {canManage && (
          <Button
            type="button"
            className={PRIMARY}
            onClick={onSend}
            disabled={lines.length === 0}
            aria-describedby={headingId}
          >
            <Send className="h-3.5 w-3.5" aria-hidden="true" />
            {t('oaw.card.send')}
          </Button>
        )}
      </div>

      {lines.length === 0 ? (
        <p className="border-t px-4 py-3 text-sm text-muted-foreground">{t('oaw.card.empty')}</p>
      ) : (
        <table className="w-full table-fixed border-t text-sm">
          <caption className="sr-only">{t('oaw.card.caption', { po: po.poNumber, supplier })}</caption>
          <thead>
            <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="px-4 py-2 text-left font-medium">{t('oaw.col.blank')}</th>
              <th scope="col" className="hidden w-48 px-3 py-2 text-left font-medium md:table-cell">{t('oaw.col.sku')}</th>
              <th scope="col" className="hidden w-56 px-3 py-2 text-left font-medium sm:table-cell">{t('oaw.col.status')}</th>
              <th scope="col" className="w-20 px-4 py-2 text-right font-medium">{t('oaw.col.qty')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.skuId} className="border-b align-top last:border-b-0">
                <td className="px-4 py-2">
                  <p className="break-words">{jitRowName(line)}</p>
                  {line.sku && <p className="break-all font-mono text-[11px] text-muted-foreground md:hidden">{line.sku}</p>}
                  <OrderAndWaitLineMarks line={line} supplierName={po.supplierName} placeholder className="mt-1 sm:hidden" />
                </td>
                <td className="hidden break-all px-3 py-2 font-mono text-xs md:table-cell">
                  {line.sku ?? <span className="font-sans text-muted-foreground">-</span>}
                </td>
                <td className="hidden px-3 py-2 sm:table-cell">
                  <OrderAndWaitLineMarks line={line} supplierName={po.supplierName} placeholder />
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{line.qty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SparesCard({ spares, ordersReturnTo }: { spares: readonly OrderAndWaitSpare[]; ordersReturnTo: string }) {
  const { t } = useTranslation('orders');
  return (
    <Card className="overflow-hidden">
      <div className="border-b px-4 py-3">
        <h3 className="text-sm font-semibold">{t('oaw.spares.title')}</h3>
        <p className="text-xs text-muted-foreground">{t('oaw.spares.help')}</p>
      </div>
      <ul className="divide-y">
        {spares.map((spare) => (
          <li
            key={`${spare.skuId ?? spare.sku}-${spare.poId ?? ''}`}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm"
          >
            <span className="min-w-0 flex-1 break-words">{jitRowName(spare)}</span>
            <StatusPill tone="off">{t('oaw.spares.count', { count: spare.qty })}</StatusPill>
            {spare.poId && spare.poNumber && (
              <span className="text-xs text-muted-foreground">
                {t('oaw.spares.from')}{' '}
                <PoNumberLink poId={spare.poId} poNumber={spare.poNumber} ordersReturnTo={ordersReturnTo} />
              </span>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
